// Minimal filter edge shape so inferred connection lines render as filter edges (FilterEdge component).
// Match workflow_examples/workflow_with_branches_and_filter.json filter edge data.fields shape
const DEFAULT_FILTER_FIELDS = [
  { name: 'Filter Name', schema: { type: 'string' } },
  {
    name: 'Only continue if...',
    schema: {
      title: 'Filter Setup',
      type: 'array',
      items: {
        title: 'Conditions',
        type: 'array',
        items: {
          title: 'Only continue if...',
          type: 'object',
          properties: {
            lhs: { title: 'Choose Field...', type: 'string' },
            conditionType: {
              title: 'Choose condition...',
              type: 'string',
              enum: [
                'stringEqual',
                'stringUnequal',
                'listContains',
                'listNotContain',
                'greaterThan',
                'lessThan',
                'isTrue',
                'isFalse',
                'valueExists',
                'valueNotExists',
              ],
              enumNames: [
                '(Text/Number) Equals',
                '(Text/Number) Does not equal',
                '(Text/Array) Contains',
                '(Text/Array) Does not contain',
                '(Number) Greater than',
                '(Number) Less Than',
                '(Boolean) Is true',
                '(Boolean) Is false',
                'Exists',
                'Does not exist',
              ],
            },
            rhs: { title: 'Enter or select value...', type: 'string' },
          },
        },
      },
    },
  },
  { name: 'Delay', schema: { title: 'Delay Setup (seconds)', type: 'string' } },
];

export { DEFAULT_FILTER_FIELDS };

function makeFilterEdge(id, source, target) {
  return {
    id,
    source,
    target,
    type: 'filter',
    animated: false,
    style: { stroke: '#0069ea', strokeWidth: 2 },
    data: {
      text: 'empty',
      fields: DEFAULT_FILTER_FIELDS.map((f, idx) => ({ ...f })),
    },
  };
}

/**
 * When a workflow has nodes but no edges (e.g. after a bad save or refresh),
 * infer edges so the canvas shows connection lines: trigger first, then actions in order.
 * Inferred edges use type 'filter' and minimal data so they render like workflow_examples.
 * @param {Array} frontendWorkflow - Array of node and edge elements (React Flow / Django shape)
 * @returns {Array} Same array with inferred edges appended if needed
 */
export function repairFrontendWorkflow(frontendWorkflow) {
  if (!Array.isArray(frontendWorkflow) || frontendWorkflow.length === 0) return frontendWorkflow;
  const nodes = frontendWorkflow.filter((el) => el && typeof el === 'object' && el.id && el.source == null && el.target == null);
  const edges = frontendWorkflow.filter((el) => el && typeof el === 'object' && (el.source || el.target));
  if (edges.length > 0 || nodes.length < 2) return frontendWorkflow;
  // Order: trigger first, then by position.y, then position.x
  const ordered = [...nodes].sort((a, b) => {
    const tA = a.type || (a.data && a.data.type) || 'action';
    const tB = b.type || (b.data && b.data.type) || 'action';
    if (tA === 'trigger' && tB !== 'trigger') return -1;
    if (tA !== 'trigger' && tB === 'trigger') return 1;
    const posA = a.position || (a.data && a.data.position) || {};
    const posB = b.position || (b.data && b.data.position) || {};
    const yA = posA.y ?? 0;
    const yB = posB.y ?? 0;
    if (yA !== yB) return yA - yB;
    return (posA.x ?? 0) - (posB.x ?? 0);
  });
  const inferredEdges = [];
  for (let i = 0; i < ordered.length - 1; i++) {
    const src = ordered[i].id;
    const tgt = ordered[i + 1].id;
    if (src && tgt) inferredEdges.push(makeFilterEdge(`e-${src}-${tgt}`, src, tgt));
  }
  return [...frontendWorkflow, ...inferredEdges];
}

/**
 * Given an array of node objects, return inferred edges (trigger -> action1 -> action2 -> ...).
 * Edges include type 'filter' and data so they render as filter connection lines.
 * Use when you already have nodes and edges as separate lists (e.g. in handleAIWorkflowGenerated).
 * @param {Array} nodes - List of node objects with id, type, position
 * @returns {Array} List of edge objects { id, source, target, type, style, data }
 */
export function inferEdgesFromNodes(nodes) {
  if (!Array.isArray(nodes) || nodes.length < 2) return [];
  const ordered = [...nodes].sort((a, b) => {
    const tA = a.type || (a.data && a.data.type) || 'action';
    const tB = b.type || (b.data && b.data.type) || 'action';
    if (tA === 'trigger' && tB !== 'trigger') return -1;
    if (tA !== 'trigger' && tB === 'trigger') return 1;
    const posA = a.position || (a.data && a.data.position) || {};
    const posB = b.position || (b.data && b.data.position) || {};
    const yA = posA.y ?? 0;
    const yB = posB.y ?? 0;
    if (yA !== yB) return yA - yB;
    return (posA.x ?? 0) - (posB.x ?? 0);
  });
  const edges = [];
  for (let i = 0; i < ordered.length - 1; i++) {
    const src = ordered[i].id;
    const tgt = ordered[i + 1].id;
    if (src && tgt) edges.push(makeFilterEdge(`e-${src}-${tgt}`, src, tgt));
  }
  return edges;
}
